import { useState, useEffect, useRef, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authClient } from "../lib/auth-client";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Loader2, Check } from "lucide-react";
import { toast } from "sonner";

// Personal email domains to block
const personalDomains = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "aol.com",
  "icloud.com",
  "mail.com",
  "protonmail.com",
];

// Validate name field (checkEmpty = true for submit, false for blur)
const validateName = (name: string, checkEmpty = true): string => {
  if (!name.trim()) {
    return checkEmpty ? "Please enter your name." : "";
  }
  if (name.trim().length < 2) {
    return "Please enter at least 2 characters.";
  }
  if (/\d/.test(name)) {
    return "Names shouldn't include numbers.";
  }
  if (/[#$%@!^&*()+=[\]{};:"\\|,.<>/?~`]/.test(name)) {
    return "Please use only letters, hyphens, or apostrophes.";
  }
  return "";
};

// Validate email field (checkEmpty = true for submit, false for blur)
const validateEmail = (emailValue: string, checkEmpty = true): string => {
  if (!emailValue.trim()) {
    return checkEmpty ? "Please enter your email." : "";
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  if (!emailRegex.test(emailValue)) {
    return "Please enter a valid email address.";
  }
  const domain = emailValue.split("@")[1]?.toLowerCase();
  if (domain && personalDomains.includes(domain)) {
    return "Please use your school or organization email.";
  }
  return "";
};

// Re-export shared username validation (aligned with sig-auth constraints)
import { validateUsername } from "../lib/username-validation";

// Validate password (checkEmpty = true for submit, false for blur)
const validatePassword = (
  passwordValue: string,
  checkEmpty = true,
): string => {
  if (!passwordValue) {
    return checkEmpty ? "Please enter a password." : "";
  }
  if (passwordValue.length < 6) {
    return "Password must be at least 6 characters.";
  }
  return "";
};

// Validate confirm password (checkEmpty = true for submit, false for blur)
const validateConfirmPassword = (
  confirmValue: string,
  passwordValue: string,
  checkEmpty = true,
): string => {
  if (!confirmValue) {
    return checkEmpty ? "Please confirm your password." : "";
  }
  if (confirmValue !== passwordValue) {
    return "Passwords don't match.";
  }
  return "";
};

export const Signup = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [isUsernameValid, setIsUsernameValid] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const latestUsernameCheckRef = useRef("");

  // Redirect if already logged in
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Check if username is taken (API call). Returns true if available.
  const checkUsernameAvailability = async (usernameValue: string): Promise<boolean> => {
    const trimmed = usernameValue.trim();
    if (!trimmed || trimmed.length < 2) return false;

    latestUsernameCheckRef.current = trimmed;
    setIsCheckingUsername(true);
    setIsUsernameValid(false);
    try {
      const { data, error } = await authClient.isUsernameAvailable({
        username: trimmed,
      });

      // Ignore stale responses from earlier requests
      if (latestUsernameCheckRef.current !== trimmed) return false;

      if (error) {
        setUsernameError("Unable to verify username availability.");
        return false;
      }

      if (data?.available) {
        setIsUsernameValid(true);
        return true;
      } else {
        setUsernameError("This username is already taken.");
        return false;
      }
    } catch {
      if (latestUsernameCheckRef.current !== trimmed) return false;
      setUsernameError("Unable to verify username availability.");
      return false;
    } finally {
      if (latestUsernameCheckRef.current === trimmed) {
        setIsCheckingUsername(false);
      }
    }
  };

  const handleFirstNameChange = (value: string) => {
    setFirstName(value);
    if (firstNameError) {
      setFirstNameError(validateName(value, false));
    }
  };

  const handleLastNameChange = (value: string) => {
    setLastName(value);
    if (lastNameError) {
      setLastNameError(validateName(value, false));
    }
  };

  const handleFirstNameBlur = () => {
    setFirstNameError(validateName(firstName, false));
  };

  const handleLastNameBlur = () => {
    setLastNameError(validateName(lastName, false));
  };

  // Email change handler with domain fullness detection
  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) {
      setEmailError("");
    }
    setIsEmailValid(false);
    if (value.includes("@") && /\.[a-z]{2,}$/i.test(value)) {
      setIsCheckingEmail(true);
      const err = validateEmail(value, false);
      if (err) {
        setEmailError(err);
        setIsCheckingEmail(false);
      } else {
        // BetterAuth does not expose a check-email endpoint.
        // Treat the email as available and rely on signup error responses.
        setIsEmailValid(true);
        setIsCheckingEmail(false);
      }
    }
  };

  const handleEmailBlur = () => {
    if (!isCheckingEmail) {
      setEmailError(validateEmail(email, false));
    }
  };

  const handleUsernameChange = (value: string) => {
    setUsername(value);
    if (usernameError) {
      setUsernameError("");
    }
    setIsUsernameValid(false);
  };

  const handleUsernameBlur = () => {
    const err = validateUsername(username, false);
    setUsernameError(err);
    if (!err) {
      checkUsernameAvailability(username);
    }
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (passwordError) {
      setPasswordError(validatePassword(value, false));
    }
    if (confirmPassword && confirmPasswordError) {
      setConfirmPasswordError(
        value !== confirmPassword ? "Passwords don't match." : "",
      );
    }
  };

  const handlePasswordBlur = () => {
    setPasswordError(validatePassword(password, false));
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    if (confirmPasswordError) {
      setConfirmPasswordError(
        validateConfirmPassword(value, password, false),
      );
    }
  };

  const handleConfirmPasswordBlur = () => {
    setConfirmPasswordError(
      validateConfirmPassword(confirmPassword, password, false),
    );
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate all fields
    const firstNameValidation = validateName(firstName);
    const lastNameValidation = validateName(lastName);
    const emailValidation = validateEmail(email);
    const usernameValidation = validateUsername(username);
    const passwordValidation = validatePassword(password);
    const confirmPasswordValidation = validateConfirmPassword(
      confirmPassword,
      password,
    );

    setFirstNameError(firstNameValidation);
    setLastNameError(lastNameValidation);
    setEmailError(emailValidation);
    setUsernameError(usernameValidation);
    setPasswordError(passwordValidation);
    setConfirmPasswordError(confirmPasswordValidation);

    if (
      firstNameValidation ||
      lastNameValidation ||
      emailValidation ||
      usernameValidation ||
      passwordValidation ||
      confirmPasswordValidation
    ) {
      return;
    }

    setIsSubmitting(true);

    // Ensure username availability has been verified before proceeding
    if (!isUsernameValid) {
      const available = await checkUsernameAvailability(username);
      if (!available) {
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const { data, error } = await authClient.signUp.email({
        username: username.trim(),
        password,
        email: email.trim(),
        name: fullName,
        callbackURL: "/dashboard",
      });

      if (error || !data?.user) {
        throw new Error(
          error?.message || "Registration failed. Please try again.",
        );
      }

      toast.success("Account created successfully! Please log in.");
      navigate("/login");
    } catch (err: unknown) {
      console.error("Registration error:", err);
      const errorMessage =
        (typeof err === "string" ? err : (err as Error)?.message) ||
        "Registration failed. Please try again.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30">
      <div className="w-full max-w-sm px-4 py-8">
        <Card className="shadow-sm">
          <CardHeader className="text-center space-y-1 pb-2">
            <CardTitle className="text-2xl font-semibold tracking-tight">
              Create your account
            </CardTitle>
            <CardDescription>Request access to the dashboard</CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First name</Label>
                  <Input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => handleFirstNameChange(e.target.value)}
                    onBlur={handleFirstNameBlur}
                    placeholder="First name"
                    autoComplete="given-name"
                  />
                  {firstNameError && (
                    <p className="text-xs text-destructive">
                      {firstNameError}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last name</Label>
                  <Input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => handleLastNameChange(e.target.value)}
                    onBlur={handleLastNameBlur}
                    placeholder="Last name"
                    autoComplete="family-name"
                  />
                  {lastNameError && (
                    <p className="text-xs text-destructive">
                      {lastNameError}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    onBlur={handleEmailBlur}
                    placeholder="Enter your school email"
                    autoComplete="email"
                  />
                  {isCheckingEmail && (
                    <div className="absolute inset-y-0 right-2 flex items-center">
                      <Loader2 className="size-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  {isEmailValid && !isCheckingEmail && !emailError && (
                    <div className="absolute inset-y-0 right-2 flex items-center">
                      <Check className="size-4 text-green-600" />
                    </div>
                  )}
                </div>
                {emailError && (
                  <p className="text-xs text-destructive">{emailError}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <div className="relative">
                  <Input
                    type="text"
                    id="username"
                    value={username}
                    onChange={(e) => handleUsernameChange(e.target.value)}
                    onBlur={handleUsernameBlur}
                    placeholder="Enter a username"
                    autoComplete="username"
                  />
                  {isCheckingUsername && (
                    <div className="absolute inset-y-0 right-2 flex items-center">
                      <Loader2 className="size-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  {isUsernameValid && !isCheckingUsername && (
                    <div className="absolute inset-y-0 right-2 flex items-center">
                      <Check className="size-4 text-green-600" />
                    </div>
                  )}
                </div>
                {usernameError ? (
                  <p className="text-xs text-destructive">{usernameError}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Letters, numbers, underscores, and periods only.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  onBlur={handlePasswordBlur}
                  placeholder="Create a password"
                  autoComplete="new-password"
                  revealWhileTyping
                />
                {passwordError && (
                  <p className="text-xs text-destructive">{passwordError}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm password</Label>
                <Input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) =>
                    handleConfirmPasswordChange(e.target.value)
                  }
                  onBlur={handleConfirmPasswordBlur}
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                />
                {confirmPasswordError && (
                  <p className="text-xs text-destructive">
                    {confirmPasswordError}
                  </p>
                )}
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <Button type="submit" disabled={isSubmitting} className="w-full">
                {isSubmitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin mr-2" />
                    Creating account...
                  </>
                ) : (
                  "Create account"
                )}
              </Button>
            </form>
          </CardContent>

          <CardFooter className="flex flex-col pt-4">
            <p className="text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};
